// import core components
import { useRef, useEffect } from "react";
import { gsap } from "gsap/dist/gsap";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";
import { MotionPathPlugin } from "gsap/dist/MotionPathPlugin";

// import custom components
import CustomImage from "@/elements/Shared/CustomImage";
import Circle from "@/elements/Shared/DesignComponents/Circle";
import LottieFilePlayer from "../LottieFilePlayer";

// import styles
import styles from "./blocktextimage.module.scss";

// import utils
import {
  handleColorStyle,
  handleDispositionContainer,
  renderButton,
  renderContent,
  renderTitleTag,
} from "@/utils/BlockTextImage";

import handleAnimations from "@/utils/BlockTextImage/handleAnimations";

// interface
import BlockTextImageProps from "@/interfaces/BlockTextImage/BlockTextImageProps";

export default function BlockText({
  badgesStyle = 1,
  id,
  children,
  title,
  button,
  images,
  circles,
  content,
  HtagLevel = 2,
  isImageAllEgal = false,
  isHero = false,
  isAlternativeSide = false,
  isAlternativeSideOnMobile = false,
  asBackgroundDegrade = false,
  isTextBlackOnly = false,
  isImageInsideContainer = true,
  asPaddingBottom = false,
  divUnderBlockText = false,
  isH2AlternativeSize = false,
  subtitleFontSizeStyle = "large",
  dispositionContainer,
  sectionName,
  lottieFile,
  optionsAnimImages = {
    type: "fadeIn",
    firstImageToAnimate: 1,
    lastImageToAnimate: 2,
    xPercent: 0,
    yPercent: 0,
  },
  isAnimated = true,
  isCircleAnimated = false,
  canAnimationBePaused = false,
  externalAnimations,
  margin = "",
}: BlockTextImageProps) {
  // Ref
  const titleRef = useRef(null);
  const imageRef = useRef(null);
  const buttonRef = useRef(null);
  const separatorRef = useRef(null);
  const containerRef = useRef(null);
  const containerImageRef = useRef(null);

  // Register ScrollTrigger
  if (typeof window !== "undefined") {
    gsap.registerPlugin(ScrollTrigger, MotionPathPlugin);
  }

  useEffect(() => {
    if (process.browser) {
      setTimeout(
        () => {
          handleAnimations({
            isHero,
            titleRef,
            buttonRef,
            isAnimated,
            sectionName,
            separatorRef,
            containerRef,
            isCircleAnimated,
            optionsAnimImages,
            containerImageRef,
            canAnimationBePaused,
            externalAnimations,
          });
        },
        isHero ? 0 : 300
      );
    }
  }, []);

  return (
    <div
      ref={containerRef}
      id={id !== "" ? id : ""}
      className={`container ${styles._container} ${handleDispositionContainer(
        isHero,
        dispositionContainer,
        styles
      )} ${isAlternativeSide ? styles._alternative : ""} ${
        isAlternativeSideOnMobile ? styles._alternative_on_mobile : ""
      }`}
    >
      <div
        className={`${styles._content} ${handleColorStyle(
          asBackgroundDegrade,
          isTextBlackOnly,
          styles
        )} ${!asPaddingBottom ? styles._no_padding : ""} ${
          isHero ? styles._padding_bottom : ""
        }`}
      >
        {renderTitleTag(
          HtagLevel,
          styles,
          isHero,
          asBackgroundDegrade,
          title,
          isH2AlternativeSize,
          sectionName,
          titleRef
        )}

        {content.map((text, i) => {
          return renderContent(
            i,
            title,
            text,
            styles,
            subtitleFontSizeStyle,
            isHero,
            sectionName,
            separatorRef
          );
        })}

        {button &&
          button?.isVisible &&
          renderButton(button, sectionName, isHero, buttonRef)}

        {divUnderBlockText && divUnderBlockText}
      </div>

      {((images && images.length >= 1) || circles || lottieFile) && (
        <div
          ref={containerImageRef}
          className={`${styles._image_container} ${
            !isImageInsideContainer ? styles._absolute : ""
          } ${!isImageInsideContainer && isHero ? "" : styles._full_height} ${
            styles[`${sectionName}-image-container`]
          } ${margin !== "" ? styles[`margin-${margin}-auto`] : ""}`}
        >
          {circles &&
            circles.map((circle, i) => {
              return (
                <Circle
                  key={i}
                  circle={circle}
                  linearGradientId={sectionName + i}
                  circleId={`${styles[`${sectionName}-circle-${i + 1}`]}`}
                  className={`${sectionName}-circle ${sectionName}-circle-${
                    i + 1
                  }`}
                  pathId={`${sectionName}-path-${i + 1}`}
                />
              );
            })}

          {lottieFile && lottieFile.name !== "" && (
            <>
              <LottieFilePlayer
                className={`${sectionName}-image`}
                lottieFile={lottieFile}
                imageRef={imageRef}
              />
            </>
          )}

          {images &&
            images.map((image, i) => {
              return (
                <CustomImage
                  image={image}
                  key={image.alt + i}
                  badgesStyle={badgesStyle}
                  isAllEgal={isImageAllEgal}
                  imageId={styles[`${sectionName}-image-${i + 1}`]}
                  imageClassName={`${sectionName}-image ${sectionName}-image-${
                    i + 1
                  }`}
                  imageRef={imageRef}
                  arrowClass={`${sectionName}-arrow`}
                  isHero={isHero}
                />
              );
            })}
        </div>
      )}
      {children && children}
    </div>
  );
}
